import { useEffect, useRef } from 'react';

/**
 * 히어로 배너 WebGL 렌더러.
 *
 * 한 프래그먼트 셰이더 안에서 두 가지를 동시에 처리한다.
 *  1) 뎁스 패럴랙스 — 이미지 휘도 + 중앙 방사 가중치로 깊이를 추정해서
 *     마우스 방향으로 UV를 밀어낸다. 밝고 중앙에 가까운 픽셀일수록 많이 움직여
 *     평면 사진이 2.5D로 살아난다. 뎁스맵을 미리 만들 필요가 없다.
 *  2) 액체 디스플레이스먼트 전환 — fbm 노이즈로 현재/다음 텍스처의 UV를
 *     서로 반대 방향으로 왜곡하면서 노이즈로 흐트러진 경계를 따라 섞는다.
 *
 * 두 효과는 UV 단계에서 자연스럽게 합성된다(패럴랙스가 먼저 UV를 밀고,
 * 그 위에 전환 왜곡이 얹힘).
 */

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG = `
precision highp float;
varying vec2 vUv;

uniform sampler2D uCur;
uniform sampler2D uNext;
uniform vec2  uCurSize;
uniform vec2  uNextSize;
uniform vec2  uRes;
uniform float uProgress;   // 0=현재 1=다음
uniform vec2  uMouse;      // -1..1 (스무딩됨)
uniform float uTime;
uniform float uHover;      // 0..1 리플 강도
uniform float uMotion;     // 0=모션 최소화 1=전체

// UV를 밀어내는 총량(패럴랙스 0.045 + 전환 변위 0.06 + 리플 0.008 = 0.113)보다
// 확대 여유분이 커야 가장자리가 CLAMP되어 줄이 늘어지지 않는다.
// ZOOM=1.30 → 한쪽 여유 0.5-0.5/1.30 = 0.115
const float ZOOM = 1.30;

// 종횡비 유지 cover 핏 + 살짝 확대
vec2 coverUv(vec2 uv, vec2 img, vec2 res) {
  float ra = res.x / max(res.y, 1.0);
  float ia = img.x / max(img.y, 1.0);
  vec2 s = (ia > ra) ? vec2(ra / ia, 1.0) : vec2(1.0, ia / ra);
  return (uv - 0.5) * s / ZOOM + 0.5;
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise(p); p *= 2.02; a *= 0.5; }
  return v;
}

// 휘도(가볍게 블러) + 중앙 방사 가중치로 깊이 추정
float depthOf(sampler2D t, vec2 uv) {
  float o = 0.006;
  vec3 c = texture2D(t, uv).rgb
         + texture2D(t, uv + vec2(o, 0.0)).rgb
         + texture2D(t, uv - vec2(o, 0.0)).rgb
         + texture2D(t, uv + vec2(0.0, o)).rgb
         + texture2D(t, uv - vec2(0.0, o)).rgb;
  c *= 0.2;
  float lum = dot(c, vec3(0.299, 0.587, 0.114));
  float radial = 1.0 - smoothstep(0.0, 0.85, length(uv - 0.5) * 1.4);
  return mix(lum, radial, 0.45);
}

void main() {
  vec2 uv = vUv;

  // 커서를 중심으로 퍼지는 잔물결
  vec2 m = uMouse * 0.5 + 0.5;
  vec2 toM = uv - m;
  float dm = length(toM);
  float rip = sin(dm * 22.0 - uTime * 2.2) * exp(-dm * 6.0) * 0.008 * uHover * uMotion;
  vec2 ripple = (dm > 0.0001 ? toM / dm : vec2(0.0)) * rip;

  // 현재 이미지의 깊이로 시차 오프셋 산출
  float dep = depthOf(uCur, coverUv(uv, uCurSize, uRes));
  vec2 par = uMouse * (dep - 0.35) * 0.045 * uMotion;

  vec2 cuv = coverUv(uv + par, uCurSize,  uRes) + ripple;
  vec2 nuv = coverUv(uv + par, uNextSize, uRes) + ripple;

  // 노이즈로 흐트러뜨린 전환 경계.
  // 노이즈를 x 그라데이션보다 크게 줘야 경계가 수직선이 아니라 유기적으로 번진다.
  float n = fbm(uv * 3.0 + uTime * 0.04);
  float val = (1.0 - uv.x) * 0.45 + n * 1.0;   // 이론적 범위 [0, 1.3875]
  const float W = 0.30;                         // 블렌드 폭

  // uProgress(0..1)를 val 범위 밖에서 밖까지 훑도록 매핑해야
  // 전환이 끝까지 완료된다(안 그러면 이전 이미지 띠가 남았다가 툭 끊김).
  float pp = mix(-0.35, 1.75, uProgress);
  float edge = smoothstep(pp - W, pp + W, val);

  // 왜곡은 전환 중반에, 그리고 경계 부근에 집중시킨다.
  // 그래야 정착된 영역은 선명하고 경계만 일렁여서 액체처럼 보인다.
  float band = 1.0 - abs(edge * 2.0 - 1.0);
  float amp = sin(uProgress * 3.14159) * 0.06 * uMotion * (0.3 + 0.7 * band);
  float ang = n * 6.28318;
  vec2 dir = vec2(cos(ang), sin(ang));

  vec2 aUv = cuv + dir * amp * (1.0 - edge);
  vec2 bUv = nuv - dir * amp * edge;

  vec3 col = mix(texture2D(uNext, bUv).rgb, texture2D(uCur, aUv).rgb, edge);

  // 변형이 가장 심한 지점에만 색수차
  float ca = amp * band * 0.35;
  col.r = mix(texture2D(uNext, bUv + dir * ca).r,
              texture2D(uCur,  aUv + dir * ca).r, edge);
  col.b = mix(texture2D(uNext, bUv - dir * ca).b,
              texture2D(uCur,  aUv - dir * ca).b, edge);

  // 비네트 + 그림자쪽 보라 틴트(브랜드 톤)
  float vig = 1.0 - smoothstep(0.45, 1.15, length(uv - 0.5) * 1.3);
  col *= mix(0.72, 1.0, vig);
  col = mix(col, col * vec3(0.72, 0.62, 1.0), 0.18);

  // 아주 옅은 그레인
  col += (hash(uv * uRes + fract(uTime)) - 0.5) * 0.022;

  gl_FragColor = vec4(col, 1.0);
}`;

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

function buildProgram(gl) {
  const vs = compile(gl, gl.VERTEX_SHADER, VERT);
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) return null;
  const p = gl.createProgram();
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  gl.linkProgram(p);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    gl.deleteProgram(p);
    return null;
  }
  return p;
}

function makeTexture(gl, source) {
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  // WebGL 텍스처 원점은 좌하단이라 이미지 첫 행이 아래로 들어간다.
  // 켜지 않으면 화면에 상하 반전되어 보인다.
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  // 사진은 2의 거듭제곱 크기가 아니므로 CLAMP + LINEAR 고정 (밉맵 불가)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
  return tex;
}

const TRANSITION_MS = 1100;
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/**
 * S3에 CORS를 켜기 전 받아둔 응답(ACAO 헤더 없음)이 브라우저 캐시에 남아 있으면,
 * crossOrigin 요청에도 그 캐시가 재사용되어 계속 CORS 오류가 난다.
 * 버전 파라미터로 캐시 키를 한 번 갈아주면 CORS 헤더가 붙은 응답을 새로 받아
 * 이후로는 정상적으로 캐시된다. (타임스탬프가 아니라 고정값이어야 캐시가 산다)
 */
const CORS_CACHE_BUST = 'v=1';
function corsUrl(u) {
  if (!u || !/^https?:\/\//i.test(u)) return u; // 상대경로(동일 오리진)는 그대로
  return u + (u.includes('?') ? '&' : '?') + CORS_CACHE_BUST;
}

export default function PaperHeroCanvas({ images, index, className, onUnsupported }) {
  const canvasRef = useRef(null);
  const S = useRef({
    gl: null, prog: null, loc: {}, quad: null,
    textures: new Map(),       // url -> { tex, w, h }
    blank: null,
    cur: null, next: null,     // { tex, w, h }
    progress: 0, transitioning: false, startedAt: 0,
    shownUrl: null,
    mouse: [0, 0], mouseTarget: [0, 0],
    hover: 0, hoverTarget: 0,
    motion: 1,
    raf: 0, t0: 0, dead: false,
  }).current;

  // ── GL 초기화 + 렌더 루프 ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl', {
      alpha: false, antialias: false, depth: false,
      stencil: false, powerPreference: 'high-performance',
    });
    if (!gl) { onUnsupported?.(); return; }

    const prog = buildProgram(gl);
    if (!prog) { onUnsupported?.(); return; }

    S.gl = gl;
    S.prog = prog;
    S.dead = false;

    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    S.quad = quad;

    gl.useProgram(prog);
    const aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    for (const k of ['uCur', 'uNext', 'uCurSize', 'uNextSize', 'uRes',
                     'uProgress', 'uMouse', 'uTime', 'uHover', 'uMotion']) {
      S.loc[k] = gl.getUniformLocation(prog, k);
    }
    gl.uniform1i(S.loc.uCur, 0);
    gl.uniform1i(S.loc.uNext, 1);

    // 텍스처가 준비되기 전에도 샘플링이 안전하도록 1x1 더미
    S.blank = makeTexture(gl, new Image());
    gl.bindTexture(gl.TEXTURE_2D, S.blank);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([13, 11, 20, 255]));

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    S.motion = mq.matches ? 0 : 1;
    const onMq = (e) => { S.motion = e.matches ? 0 : 1; };
    mq.addEventListener?.('change', onMq);

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // 캔버스 위에 콘텐츠가 덮여 있으므로 포인터는 컨테이너에서 받는다
    const hit = canvas.parentElement || canvas;
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      if (!r.width || !r.height) return;
      S.mouseTarget = [
        ((e.clientX - r.left) / r.width) * 2 - 1,
        -(((e.clientY - r.top) / r.height) * 2 - 1),
      ];
      S.hoverTarget = 1;
    };
    const onLeave = () => { S.hoverTarget = 0; };
    hit.addEventListener('pointermove', onMove);
    hit.addEventListener('pointerleave', onLeave);

    S.t0 = performance.now();

    const frame = (now) => {
      if (S.dead) return;
      S.raf = requestAnimationFrame(frame);

      const t = (now - S.t0) / 1000;

      // 커서가 없을 땐 느린 자동 드리프트로 화면이 멈춰 보이지 않게
      if (S.hoverTarget === 0) {
        S.mouseTarget = [Math.sin(t * 0.31) * 0.5, Math.cos(t * 0.23) * 0.35];
      }
      S.mouse[0] += (S.mouseTarget[0] - S.mouse[0]) * 0.055;
      S.mouse[1] += (S.mouseTarget[1] - S.mouse[1]) * 0.055;
      S.hover += (S.hoverTarget - S.hover) * 0.08;

      if (S.transitioning) {
        const k = Math.min(1, (now - S.startedAt) / (S.motion ? TRANSITION_MS : 320));
        S.progress = easeInOut(k);
        if (k >= 1) {
          S.transitioning = false;
          S.progress = 0;
          if (S.next) S.cur = S.next;
        }
      }

      const cur = S.cur, next = S.next || S.cur;
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, cur ? cur.tex : S.blank);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, next ? next.tex : S.blank);

      gl.uniform2f(S.loc.uCurSize, cur ? cur.w : 1, cur ? cur.h : 1);
      gl.uniform2f(S.loc.uNextSize, next ? next.w : 1, next ? next.h : 1);
      gl.uniform2f(S.loc.uRes, canvas.width, canvas.height);
      gl.uniform1f(S.loc.uProgress, S.progress);
      gl.uniform2f(S.loc.uMouse, S.mouse[0], S.mouse[1]);
      gl.uniform1f(S.loc.uTime, t);
      gl.uniform1f(S.loc.uHover, S.hover);
      gl.uniform1f(S.loc.uMotion, S.motion);

      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };
    S.raf = requestAnimationFrame(frame);

    return () => {
      S.dead = true;
      cancelAnimationFrame(S.raf);
      ro.disconnect();
      mq.removeEventListener?.('change', onMq);
      hit.removeEventListener('pointermove', onMove);
      hit.removeEventListener('pointerleave', onLeave);
      S.textures.forEach(({ tex }) => gl.deleteTexture(tex));
      S.textures.clear();
      if (S.blank) gl.deleteTexture(S.blank);
      if (S.quad) gl.deleteBuffer(S.quad);
      if (S.prog) gl.deleteProgram(S.prog);
      S.gl = null; S.prog = null; S.cur = null; S.next = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── index 변경 시 전환 ──
  useEffect(() => {
    const url = images?.[index];
    if (!url || !S.gl || url === S.shownUrl) return;

    let cancelled = false;

    const apply = (entry) => {
      if (cancelled || S.dead || !S.gl) return;
      S.shownUrl = url;
      if (!S.cur) {
        // 최초 1장은 전환 없이 바로 표시
        S.cur = entry;
        S.next = entry;
        S.progress = 0;
        return;
      }
      S.next = entry;
      S.progress = 0;
      S.transitioning = true;
      S.startedAt = performance.now();
    };

    const cached = S.textures.get(url);
    if (cached) { apply(cached); return; }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled || S.dead || !S.gl) return;
      try {
        const entry = {
          tex: makeTexture(S.gl, img),
          w: img.naturalWidth || 1,
          h: img.naturalHeight || 1,
        };
        S.textures.set(url, entry);
        apply(entry);
      } catch {
        // CORS 등으로 텍스처 업로드 실패 → CSS 배경으로 폴백
        onUnsupported?.();
      }
    };
    img.onerror = () => { if (!cancelled) onUnsupported?.(); };
    img.src = corsUrl(url);

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, index]);

  // 다음 슬라이드 텍스처 미리 로드
  useEffect(() => {
    if (!images?.length || !S.gl) return;
    const nextUrl = images[(index + 1) % images.length];
    if (!nextUrl || S.textures.has(nextUrl)) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (S.dead || !S.gl || S.textures.has(nextUrl)) return;
      try {
        S.textures.set(nextUrl, {
          tex: makeTexture(S.gl, img),
          w: img.naturalWidth || 1,
          h: img.naturalHeight || 1,
        });
      } catch { /* 무시 — 실제 전환 때 폴백 처리됨 */ }
    };
    img.src = corsUrl(nextUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, index]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
